import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { CATEGORY, COMMENTS, NAME, PRIORITY, TERM } from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import {
  useCreatePredefinedTaskMutation,
  useUpdatePredefinedTaskMutation,
  useGetPredefinedTaskByIdQuery,
} from "@/services";
import { useEffect } from "react";
import { useAppDispatch } from "@/redux/hooks";
import { openSnackbarAction } from "@/redux/slices";
import { useTaskCategoryQuery, useTaskTermQuery } from "@/services/masterData";
import { PRIORITY_TYPES } from "./const";
import { useQueryClient } from "@tanstack/react-query";

const useAddEdit = () => {
  const { action, id } = useParams<{ action: string; id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const isView = action === routes.view;
  const isEdit = action === routes.edit;

  // Fetch categories & terms
  const { data: categoriesRes, isLoading: isLoadingCategories } = useTaskCategoryQuery({});
  const { data: termsRes, isLoading: isLoadingTerms } = useTaskTermQuery({});

  const categories = categoriesRes?.data?.[0]?.records?.map((item: any) => ({
    label: item.name,
    value: item._id,
  })) || [];

  const terms = termsRes?.data?.[0]?.records?.map((item: any) => ({
    label: item.taskTerm,
    value: item._id,
  })) || [];

  const priorityOptions = Object.values(PRIORITY_TYPES).map((p) => ({
    label: p.charAt(0).toUpperCase() + p.slice(1).toLowerCase(),
    value: p,
  }));

  // Form initialization
  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [CATEGORY]: "",
      [PRIORITY]: "",
      [TERM]: "",
      [COMMENTS]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);

  // API mutations
  const { mutateAsync: createTask, isPending: isCreating } = useCreatePredefinedTaskMutation();
  const { mutateAsync: updateTask, isPending: isUpdating } = useUpdatePredefinedTaskMutation();

  // Fetch task data if editing/viewing
  const { data: taskData, isLoading: isLoadingTask } = useGetPredefinedTaskByIdQuery(id || "");

  // Populate form when task data is fetched
  useEffect(() => {
  if ((isEdit || isView) && taskData?.data) {
    const task = taskData.data;

    form.reset({
      [NAME]: task.taskName || "",
      [CATEGORY]: task.taskCategory?._id || "",
      [PRIORITY]: task.priority || "",
      [TERM]: task.taskTerm?._id || "",
      [COMMENTS]: task.taskDescription || "",
    });
  }
}, [isEdit, isView, taskData, form]);



  const onBackClick = () => {
    navigate(-1);
  };

  const onSubmit = async (data: formValues) => {
    try {
      const cleanedData = setEmptyFieldsToNull(removeEmptyFields(data));

      if (isEdit && id) {
  await updateTask({
    id,
    taskName: cleanedData[NAME],
    taskCategory: cleanedData[CATEGORY],
    priority: cleanedData[PRIORITY],
    taskTerm: cleanedData[TERM],
    taskDescription: cleanedData[COMMENTS] || "",
  });

  dispatch(openSnackbarAction({ message: "Task updated successfully!", severity: "success" }));
}
 else {
        await createTask(cleanedData);
        dispatch(openSnackbarAction({ message: "Task created successfully!", severity: "success" }));
      }

      // Refresh the task table
      queryClient.invalidateQueries({ queryKey: ["predefinedTasks"] });

      // Navigate back
      navigate(-1);
    } catch (error: any) {
      console.error("Error submitting task:", error);
      dispatch(
        openSnackbarAction({
          message: error?.response?.data?.message || "An error occurred while saving the task",
          severity: "error",
        })
      );
    }
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isLoading: isCreating || isUpdating || isLoadingTask || isLoadingCategories || isLoadingTerms,
    categories,
    terms,
    priorityOptions,
  };
};

export default useAddEdit;
